// ── PHONE NUMBERS ────────────────────────────────────────────────────────────
// Turning what somebody types into the E.164 string Firebase demands, and
// saying no BEFORE a doomed SMS is sent.
//
// WHY VALIDATE AT ALL, when Firebase validates too: every send costs money and
// counts against a per-project quota, and `auth/invalid-phone-number` comes back
// as an opaque failure after the round trip. A wrong length is knowable here,
// instantly and for free.
//
// WHAT PEOPLE ACTUALLY TYPE is the whole problem. A phone number has one
// international form and several domestic ones, and users type the domestic one
// because that is the one written on things:
//
//   +374 77 12 34 56   the E.164 form, already correct
//   077 12 34 56       the TRUNK PREFIX form — a digit that exists only for
//                      dialling inside the country and is NOT part of the
//                      international number
//   00374 77123456     the other international prefix, used across Europe
//   374 77123456       the country code without its plus
//
// Every one of those means the same number, and only the first is one Firebase
// accepts. Silently repairing the rest is the difference between a phone field
// that works and one that rejects the number printed on the user's own SIM.
//
// THE TRUNK PREFIX IS NOT ALWAYS ZERO. It is 0 across most of Europe, 8 in
// Russia, Belarus and Kazakhstan, and ABSENT in Italy, Spain, Greece and
// Poland, where the leading digit belongs to the number itself. Assuming 0
// rejected every Russian number written the way Russians write it, and stripping
// blindly would have mangled the countries that have none — so it is a per-
// country field, applied only when the result is a valid length.

/** Armenia. The app's home market, so it is what the picker opens on. */
export const DEFAULT_DIAL = '+374';

// `nsn` is the national significant number's digit count — the part AFTER the
// country code, with no trunk prefix. A range because some countries genuinely
// vary; where a country is fixed-length both ends are the same, which is what
// makes "you typed too few digits" answerable before sending.
export const COUNTRY_CODES = [
  { iso: 'AM', dial: '+374',  name: 'Armenia',        flag: '🇦🇲', nsn: [8, 8],   trunk: '0' },
  { iso: 'RU', dial: '+7',    name: 'Russia',         flag: '🇷🇺', nsn: [10, 10], trunk: '8' },
  { iso: 'GE', dial: '+995',  name: 'Georgia',        flag: '🇬🇪', nsn: [9, 9],   trunk: '0' },
  { iso: 'US', dial: '+1',    name: 'United States',  flag: '🇺🇸', nsn: [10, 10], trunk: '1' },
  { iso: 'GB', dial: '+44',   name: 'United Kingdom', flag: '🇬🇧', nsn: [10, 10], trunk: '0' },
  { iso: 'DE', dial: '+49',   name: 'Germany',        flag: '🇩🇪', nsn: [10, 11], trunk: '0' },
  { iso: 'FR', dial: '+33',   name: 'France',         flag: '🇫🇷', nsn: [9, 9],   trunk: '0' },
  // No trunk prefix: an Italian landline's leading 0 is part of the number.
  { iso: 'IT', dial: '+39',   name: 'Italy',          flag: '🇮🇹', nsn: [9, 10],  trunk: null },
  { iso: 'ES', dial: '+34',   name: 'Spain',          flag: '🇪🇸', nsn: [9, 9],   trunk: null },
  { iso: 'NL', dial: '+31',   name: 'Netherlands',    flag: '🇳🇱', nsn: [9, 9],   trunk: '0' },
  { iso: 'GR', dial: '+30',   name: 'Greece',         flag: '🇬🇷', nsn: [10, 10], trunk: null },
  { iso: 'PL', dial: '+48',   name: 'Poland',         flag: '🇵🇱', nsn: [9, 9],   trunk: null },
  { iso: 'UA', dial: '+380',  name: 'Ukraine',        flag: '🇺🇦', nsn: [9, 9],   trunk: '0' },
  { iso: 'BY', dial: '+375',  name: 'Belarus',        flag: '🇧🇾', nsn: [9, 9],   trunk: '8' },
  // Shares +7 with Russia — same national length, same trunk digit, different
  // area codes. Suffixed for the same reason Canada is, see `dialOf`.
  { iso: 'KZ', dial: '+7KZ',  name: 'Kazakhstan',     flag: '🇰🇿', nsn: [10, 10], trunk: '8' },
  { iso: 'TR', dial: '+90',   name: 'Türkiye',        flag: '🇹🇷', nsn: [10, 10], trunk: '0' },
  { iso: 'IR', dial: '+98',   name: 'Iran',           flag: '🇮🇷', nsn: [10, 10], trunk: '0' },
  { iso: 'AE', dial: '+971',  name: 'UAE',            flag: '🇦🇪', nsn: [9, 9],   trunk: '0' },
  { iso: 'LB', dial: '+961',  name: 'Lebanon',        flag: '🇱🇧', nsn: [7, 8],   trunk: '0' },
  { iso: 'IL', dial: '+972',  name: 'Israel',         flag: '🇮🇱', nsn: [9, 9],   trunk: '0' },
  { iso: 'CA', dial: '+1CA',  name: 'Canada',         flag: '🇨🇦', nsn: [10, 10], trunk: '1' },
];

// Canada shares +1 with the United States and Kazakhstan shares +7 with Russia,
// so those entries need a distinct value to be selectable in a <select> at all
// — the trailing letters are that key, and this is where they turn back into a
// real dialling code. Nothing outside this module sees the suffixed form.
export const dialOf = (value) => String(value || '').replace(/[A-Z]+$/, '');

export const digitsOf = (v) => String(v || '').replace(/\D+/g, '');

export const countryOf = (value) =>
  COUNTRY_CODES.find(c => c.dial === value) || null;

/**
 * The national part of what was typed, with the things people actually type
 * stripped off. Conservative on purpose: each strip is applied ONLY when what
 * remains is a plausible length for that country, so a number that merely
 * happens to begin with the country's own digits is left alone.
 */
export function normalizeNational(dialValue, raw) {
  const country = countryOf(dialValue);
  const [min, max] = country?.nsn || [6, 14];
  const cc = digitsOf(dialOf(dialValue));
  let d = digitsOf(raw);
  if (!d) return '';

  const fits = (s) => s.length >= min && s.length <= max;

  // "00374…" — unambiguous, so it needs no length check to be safe.
  if (cc && d.startsWith(`00${cc}`)) {
    d = d.slice(2 + cc.length);
  } else if (cc && d.startsWith(cc) && fits(d.slice(cc.length))) {
    // "374 77123456" or a pasted "+374…" whose plus the digit strip removed.
    // Guarded by `fits`: a Russian number beginning 7… must not lose its first
    // digit just because the country code is also 7.
    d = d.slice(cc.length);
  }

  // The trunk prefix, once the number is otherwise international. Per country
  // and stripped only when doing so produces a valid length, which is what lets
  // a Russian area code that genuinely begins with 8 (812, St Petersburg)
  // survive while the dialling 8 in front of it does not.
  const trunk = country?.trunk;
  if (trunk && d.startsWith(trunk) && fits(d.slice(trunk.length))) {
    d = d.slice(trunk.length);
  }

  return d;
}

const E164_SHAPE = /^\+[1-9]\d{7,14}$/;

/**
 * An EXPLICIT international number — one written with a leading `+` or `00` —
 * resolved against the country table, ignoring whatever the picker says.
 *
 * Somebody who types the `+` has stated their country, and the picker is not it.
 * Without this, pasting a Russian `+7 916…` while the picker still says Armenia
 * was simply rejected as "not a valid Armenian number", which is true and
 * useless. Returns '' when there is no explicit prefix or nothing matches, so
 * the caller falls back to the picker.
 */
function matchInternational(raw) {
  const s = String(raw || '').trim();
  const d = digitsOf(s);
  if (!d) return null;
  if (!s.startsWith('+') && !d.startsWith('00')) return null;

  const rest = d.startsWith('00') ? d.slice(2) : d;
  // Longest dialling code first, so +374 is tried before +37 would be.
  const byLength = [...COUNTRY_CODES].sort(
    (a, b) => dialOf(b.dial).length - dialOf(a.dial).length,
  );
  for (const c of byLength) {
    const cc = digitsOf(dialOf(c.dial));
    if (!rest.startsWith(cc)) continue;
    const national = rest.slice(cc.length);
    if (national.length >= c.nsn[0] && national.length <= c.nsn[1]) {
      const e164 = `+${cc}${national}`;
      if (E164_SHAPE.test(e164)) return { country: c, national, e164 };
    }
  }
  return null;
}

function fromExplicitInternational(raw) {
  return matchInternational(raw)?.e164 || '';
}

/**
 * Splits a typed number into the country the picker should show and the part
 * that belongs in its input.
 *
 * For prefilling a phone form from somewhere else — the combined sign-in field
 * hands over whatever was typed, and this is what turns "+7 916 123 45 67"
 * into Russia + "9161234567" rather than an unparsed blob under Armenia.
 * Anything without an explicit country falls back to `fallbackDial`, which is
 * the right reading of a bare local number.
 */
export function splitPhone(raw, fallbackDial = DEFAULT_DIAL) {
  const hit = matchInternational(raw);
  if (hit) {
    // The picker's value, not the bare dialling code — Canada and Kazakhstan
    // share theirs with a neighbour and are keyed with a suffix.
    const option = COUNTRY_CODES.find(c => c.iso === hit.country.iso);
    return { dial: option ? option.dial : hit.country.dial, national: hit.national };
  }
  return { dial: fallbackDial, national: normalizeNational(fallbackDial, raw) || digitsOf(raw) };
}

/**
 * Is this a phone number rather than an email address?
 *
 * Used by the combined sign-in field to decide which credential the reader is
 * offering, so it must be decisive on PARTIAL input — it runs on every
 * keystroke, while the value is still half-typed.
 *
 * An `@` settles it: no phone number contains one, and a half-typed address
 * should keep looking like an address rather than flickering between the two.
 * Otherwise it is a phone only if every character could belong to one AND
 * there are enough digits to mean something. Six is short enough to catch the
 * shortest national numbers here and long enough that a house number typed
 * into the wrong field does not switch the form.
 */
export function looksLikePhone(value) {
  const s = String(value || '').trim();
  if (!s || s.includes('@')) return false;
  // A leading `(` counts: "(077) 12 34 56" is an ordinary way to write a
  // number, and requiring the first character to be a digit or a plus rejected
  // it. Letters are what actually rule a value out, and they still do.
  if (!/^[+(\d][\d\s\-().]*$/.test(s)) return false;
  return digitsOf(s).length >= 6;
}

/** The E.164 string Firebase takes, or '' when there is nothing usable yet. */
export function toE164(dialValue, raw) {
  const explicit = fromExplicitInternational(raw);
  if (explicit) return explicit;
  const national = normalizeNational(dialValue, raw);
  return national ? `${dialOf(dialValue)}${national}` : '';
}

/**
 * E.164 from ONE free-text field, with no country picker in play — the
 * "auto-prepend +374 if the user didn't give a country code" case. An explicit
 * `+` or `00` wins; anything else is read as a local Armenian number.
 *
 * Exported for callers that have a single input (a deep link, a pasted number,
 * a future one-field form). The panel uses the picker-aware `toE164`, which is
 * strictly better when there IS a picker: it can say "too short for Armenia"
 * instead of guessing.
 */
export function toE164Loose(raw, fallbackDial = DEFAULT_DIAL) {
  return toE164(fallbackDial, raw);
}

/**
 * What is wrong with this number, as an i18n key — or '' when nothing is.
 * Returns a key rather than a sentence so the caller translates it, and so the
 * same answer can be reused by anything else that needs to check a number.
 */
export function phoneProblem(dialValue, raw) {
  if (!digitsOf(raw)) return 'phone_err_required';

  // An explicit international number validates as itself, not against whatever
  // the picker happens to show.
  if (fromExplicitInternational(raw)) return '';
  // A LEADING `+` IS THE ONLY UNAMBIGUOUS STATEMENT of country, so it is the
  // only one that forecloses the picker: written as international but matching
  // no country, or the wrong length for the one it names, is "invalid" rather
  // than "too short for Armenia".
  //
  // A leading `00` is NOT treated that way, because it is only sometimes an
  // international prefix — a national number may simply begin with two zeros,
  // and rejecting those outright is what this guard did when it was written to
  // cover both. When `00…` fails to resolve to a country it falls through to
  // the picker, which is the reading that can still be right.
  if (String(raw || '').trim().startsWith('+')) return 'phone_err_invalid';

  const national = normalizeNational(dialValue, raw);
  if (!national) return 'phone_err_required';

  const country = countryOf(dialValue);
  const [min, max] = country?.nsn || [6, 14];
  if (national.length < min || national.length > max) return 'phone_err_invalid';

  // The shape the spec actually requires: a plus, a non-zero country code, and
  // 8–15 digits in total. Belt and braces over the per-country check above.
  if (!E164_SHAPE.test(`${dialOf(dialValue)}${national}`)) return 'phone_err_invalid';
  return '';
}

// ── SIGNING IN WITH A PHONE NUMBER AND A PASSWORD ────────────────────────────
// FIREBASE AUTH CANNOT DO THIS DIRECTLY. Its phone provider is one-time-code
// only — there is no signInWithPhoneNumberAndPassword, and no password field on
// a phone credential. The two honest ways to add one are:
//
//   1. Store and verify passwords yourself, in a Cloud Function, and mint a
//      custom token. That means owning password hashing, timing-safe
//      comparison, rate limiting and breach response — reimplementing the one
//      part of Firebase Auth that is hardest to get right.
//
//   2. Give the number an EMAIL-SHAPED ALIAS and link an email/password
//      credential to the SAME Auth account. Firebase then stores and checks the
//      password exactly as it does for anyone else, the uid does not change,
//      and the reader gets both doors: the code, or the password.
//
// This is (2). The alias is derived, never chosen and never shown — the reader
// types their phone number, and this is the string the SDK is handed.
//
// THE DOMAIN MUST NOT BE A REAL MAILBOX. Nothing is ever sent to it; it exists
// only because Firebase requires the identifier to look like an address. It is
// also refused by the registration form (see isPhoneAlias), so nobody can
// register the alias of somebody else's number as their "email" and take the
// account.
export const PHONE_ALIAS_DOMAIN = 'phone.tunify.app';

/** The internal identifier for a number. `+374 77123456` → `37477123456@…`. */
export function phoneAlias(e164) {
  const d = digitsOf(e164);
  return d ? `${d}@${PHONE_ALIAS_DOMAIN}` : '';
}

/** Is this address one of ours? Used to keep it out of the email sign-up. */
export function isPhoneAlias(email) {
  return String(email || '').trim().toLowerCase().endsWith(`@${PHONE_ALIAS_DOMAIN}`);
}

/** For display — "+374 77 12 34 56". Grouping only, never used for sending. */
export function formatE164(e164) {
  const s = String(e164 || '');
  const m = s.match(/^(\+\d{1,3})(\d+)$/);
  if (!m) return s;
  return `${m[1]} ${m[2].replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
}
